import { Exchange } from '@/types/generated/common/types'
import type {
  OrderbookDepthTierSeries,
  OrderbookMetricsSeries,
  OrderbookMetricsSeriesRequest,
} from '@/types/generated/public_api'

/**
 * Orderbook-metrics series (read-only) — see
 * docs/orderbook-metrics-and-chart-families-frontend.md.
 *
 * Depth tiers are fixed at write time to these 7 bps buckets; asking for any
 * other value, or the same tier twice, is a server-side BadRequest. Validate
 * client-side before the round-trip.
 */
export const ORDERBOOK_DEPTH_TIERS_BPS = [5, 10, 25, 50, 100, 250, 500] as const

/** Default depth tier for the ob-metrics study (input defval + fetch fallback). */
export const DEFAULT_DEPTH_TIER_BPS = 25

export type OrderbookDepthTierBps = (typeof ORDERBOOK_DEPTH_TIERS_BPS)[number]

export function isSupportedDepthTier(bps: number): bps is OrderbookDepthTierBps {
  return (ORDERBOOK_DEPTH_TIERS_BPS as readonly number[]).includes(bps)
}

/**
 * Returns an error message if the requested depth tiers are invalid, else null.
 * Mirrors the backend validator (validate_ob_metrics_request): rejects any tier
 * outside the 7 known buckets, and rejects duplicates (they alias-collide in the
 * generated SQL). An empty list is valid — it means "no depth columns".
 */
export function validateDepthTiers(tiers: readonly number[]): string | null {
  const seen = new Set<number>()
  for (const bps of tiers) {
    if (!isSupportedDepthTier(bps)) {
      return `unsupported depth tier ${bps}bps; supported: [${ORDERBOOK_DEPTH_TIERS_BPS.join(', ')}]`
    }
    if (seen.has(bps)) {
      return `duplicate depth tier: ${bps}bps`
    }
    seen.add(bps)
  }
  return null
}

export interface OrderbookMetricsRequestInput {
  exchange: Exchange
  symbol: string
  timeframe: number
  depthTiersBps?: number[]
  fromMs?: number
  toMs?: number
  limit?: number
}

/**
 * Build a validated OrderbookMetricsSeriesRequest. Throws if the depth tiers are
 * unsupported/duplicated so the caller never round-trips a guaranteed BadRequest.
 */
export function buildOrderbookMetricsRequest(
  input: OrderbookMetricsRequestInput
): OrderbookMetricsSeriesRequest {
  const depthTiersBps = input.depthTiersBps ?? []
  const tierError = validateDepthTiers(depthTiersBps)
  if (tierError) throw new Error(tierError)

  return {
    exchange: input.exchange,
    symbol: input.symbol,
    timeframe: input.timeframe,
    depthTiersBps,
    fromMs: input.fromMs !== undefined ? String(input.fromMs) : undefined,
    toMs: input.toMs !== undefined ? String(input.toMs) : undefined,
    limit: input.limit,
  }
}

export interface OrderbookDepthTierValues {
  bps: number
  bidVal: number
  askVal: number
  bidCnt: number
  askCnt: number
}

export interface OrderbookMetricsPoint {
  /** Bucket timestamp in MILLISECONDS (unlike CandleList.ticks which is seconds). */
  tickMs: number
  spreadBps: number
  spreadBpsMax: number
  pressureRatio: number
  bidLiquidityTotal: number
  askLiquidityTotal: number
  midPrice: number
  /** Per-bps-tier resting liquidity + order counts, keyed by bps. */
  tiers: Record<number, OrderbookDepthTierValues>
}

function at(arr: number[] | undefined, i: number): number {
  const v = arr?.[i]
  return typeof v === 'number' && Number.isFinite(v) ? v : NaN
}

/**
 * Zip the response's parallel arrays into per-bucket points. `ticks_ms` drives
 * the length; each depth tier's arrays are aligned by index. Values are plain
 * doubles (chart parallel-array convention), not decimal strings.
 */
export function orderbookMetricsSeriesToPoints(
  series: OrderbookMetricsSeries | undefined
): OrderbookMetricsPoint[] {
  if (!series) return []
  const n = series.ticksMs.length
  const tiers: OrderbookDepthTierSeries[] = series.depthTiers ?? []

  const points: OrderbookMetricsPoint[] = []
  for (let i = 0; i < n; i++) {
    const tickMs = Number(series.ticksMs[i])
    if (!Number.isFinite(tickMs)) continue

    const tierValues: Record<number, OrderbookDepthTierValues> = {}
    for (const tier of tiers) {
      tierValues[tier.bps] = {
        bps: tier.bps,
        bidVal: at(tier.bidVal, i),
        askVal: at(tier.askVal, i),
        bidCnt: at(tier.bidCnt, i),
        askCnt: at(tier.askCnt, i),
      }
    }

    points.push({
      tickMs,
      spreadBps: at(series.spreadBps, i),
      spreadBpsMax: at(series.spreadBpsMax, i),
      pressureRatio: at(series.pressureRatio, i),
      bidLiquidityTotal: at(series.bidLiquidityTotal, i),
      askLiquidityTotal: at(series.askLiquidityTotal, i),
      midPrice: at(series.midPrice, i),
      tiers: tierValues,
    })
  }
  return points
}
